import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Target, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_DOT = { high: "#dc2626", medium: "#d97706", low: "#3b82f6" };

function timeRemaining(targetDate) {
  if (!targetDate) return null;
  const diffDays = Math.round((new Date(targetDate) - new Date()) / 86400000);
  if (diffDays < 0)  return { text: `Overdue ${Math.abs(diffDays)}d`, overdue: true };
  if (diffDays === 0) return { text: "Due today", overdue: false };
  if (diffDays < 30)  return { text: `${diffDays}d left`, overdue: false };
  const months = Math.round(diffDays / 30);
  return { text: `${months}mo left`, overdue: false };
}

export default function GoalProgressWidget({ token, onNavigate }) {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/goals/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGoals(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    if (token) fetchGoals();
  }, [token]);

  if (loading || goals.length === 0) {
    return (
      <div style={{
        background: "rgba(255,255,255,0.82)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        border: "1px solid rgba(255,255,255,0.7)", borderRadius: "0.875rem",
        boxShadow: "0 4px 24px rgba(26,77,46,0.06)", overflow: "hidden",
      }}>
        <div style={{ padding: "0.875rem 1.25rem", borderBottom: "1px solid rgba(26,77,46,0.08)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Target size={16} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>Goal Progress</span>
        </div>
        <div style={{ padding: "1.25rem", textAlign: "center" }}>
          <p style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.85rem", margin: "0 0 0.875rem" }}>
            {loading ? "Loading..." : "Set your first goal to track big-picture progress"}
          </p>
          {!loading && (
            <button
              onClick={() => onNavigate("goals")}
              style={{
                background: "#1A4D2E", color: "#fff", border: "none", borderRadius: "2rem",
                padding: "0.5rem 1.25rem", fontFamily: "'Manrope', sans-serif",
                fontWeight: 700, fontSize: "0.8rem", cursor: "pointer",
              }}
            >
              Create Goal
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="goal-progress-widget"
      style={{
        background: "rgba(255,255,255,0.82)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        border: "1px solid rgba(255,255,255,0.7)", borderRadius: "0.875rem",
        boxShadow: "0 4px 24px rgba(26,77,46,0.06)", overflow: "hidden",
      }}
    >
      {/* Header */}
      <div style={{ padding: "0.875rem 1.25rem", borderBottom: "1px solid rgba(26,77,46,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Target size={16} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>Goal Progress</span>
        </div>
        <button
          onClick={() => onNavigate("goals")}
          style={{
            background: "none", border: "none", cursor: "pointer",
            fontFamily: "'Manrope', sans-serif", color: "#3d6b1f", fontSize: "0.78rem",
            fontWeight: 600, display: "flex", alignItems: "center", gap: "0.25rem",
          }}
        >
          View All <ArrowRight size={12} />
        </button>
      </div>

      {/* Mini goal cards grid */}
      <div style={{ padding: "0.875rem 1.25rem", display: "grid", gridTemplateColumns: `repeat(${Math.min(goals.length, 3)}, 1fr)`, gap: "0.75rem" }}>
        {goals.map((goal) => {
          const doneItems = (goal.items || []).filter((i) => i.is_completed).length;
          const totalItems = (goal.items || []).length;
          const timeLeft = timeRemaining(goal.target_date);

          return (
            <div
              key={goal.id}
              data-testid={`goal-mini-card-${goal.id}`}
              onClick={() => onNavigate("goals")}
              style={{
                background: "rgba(255,255,255,0.7)", borderRadius: "0.625rem",
                border: "1px solid rgba(26,77,46,0.08)", padding: "0.875rem",
                cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(26,77,46,0.12)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
            >
              {/* Priority dot + title */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: "0.35rem", marginBottom: "0.5rem" }}>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: PRIORITY_DOT[goal.priority] || "#d97706", flexShrink: 0, marginTop: "4px" }} />
                <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.82rem", fontWeight: 700, lineHeight: 1.3 }}>
                  {goal.title}
                </span>
              </div>

              {/* Progress bar */}
              <div style={{ height: "6px", background: "rgba(26,77,46,0.1)", borderRadius: "3px", overflow: "hidden", marginBottom: "0.3rem" }}>
                <div style={{
                  height: "100%", width: `${goal.progress_percentage}%`,
                  background: "linear-gradient(90deg, #1A4D2E, #86c559)", borderRadius: "3px",
                  transition: "width 0.5s",
                }} />
              </div>

              {/* Stats */}
              <div style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.7rem" }}>
                {totalItems > 0 && `${doneItems}/${totalItems} done`}
              </div>
              {timeLeft && (
                <div style={{ fontFamily: "'Manrope', sans-serif", color: timeLeft.overdue ? "#dc2626" : "#6b7280", fontSize: "0.7rem", marginTop: "0.2rem", display: "flex", alignItems: "center", gap: "0.2rem" }}>
                  {timeLeft.text}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
